import { useState } from 'react'
import { Dialog } from 'radix-ui'
import { X } from 'lucide-react'
import type { SubjectType } from '../types'
import { useRevokeFieldVerification } from '../hooks'

export function RevokeVerificationDialog({
  verificationId,
  subjectType,
  subjectId,
  onClose,
  onRevoked,
}: {
  verificationId: number
  subjectType: SubjectType
  subjectId: number
  onClose: () => void
  onRevoked: () => void
}) {
  const revoke = useRevokeFieldVerification(subjectType, subjectId)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  const submit = () => {
    setError(null)
    revoke.mutate(
      { id: verificationId, body: { reason: reason.trim() } },
      {
        onSuccess: () => {
          onRevoked()
          onClose()
        },
        onError: e => setError(formatError(e)),
      },
    )
  }

  return (
    <Dialog.Root open onOpenChange={o => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-slate-950/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded bg-white p-5 shadow-xl">
          <div className="mb-4 flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold text-slate-900">
              Cofnij weryfikację
            </Dialog.Title>
            <button
              type="button"
              onClick={onClose}
              className="text-slate-400 hover:text-slate-700"
              aria-label="Zamknij"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <label className="text-xs font-medium text-slate-600">
            Powód (min 3 znaki)
          </label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            maxLength={500}
            rows={4}
            className="mt-1 w-full rounded border border-slate-300 p-2 text-sm focus:border-red-500 focus:outline-none"
            placeholder="np. Klient dosłał poprawne dokumenty..."
          />
          <p className="mt-1 text-xs text-slate-400">{reason.length}/500</p>

          {error && <p className="mt-2 text-xs text-red-600">{error}</p>}

          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
            >
              Anuluj
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={reason.trim().length < 3 || revoke.isPending}
              className="rounded bg-red-600 px-3 py-1.5 text-sm text-white hover:bg-red-700 disabled:opacity-50"
            >
              {revoke.isPending ? 'Cofnąwszy...' : 'Cofnij weryfikację'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function formatError(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const r = (e as { response: { data?: { title?: string; detail?: string } } }).response
    return r?.data?.detail ?? r?.data?.title ?? 'Błąd cofania weryfikacji'
  }
  return 'Nieznany błąd'
}
